import { useNavHeightContext } from 'common/contexts/NavHeightContext';
import { BANNER_HEIGHT } from 'layouts/AppPageTemplate/utils/config';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { usePermissions } from 'utils/permissions';
import { useUserContext } from 'common/contexts/UserContext';
import { GlobalStorageKeys, LocalStorageUtil } from 'utils/local-storage.util';
import { FeatureFlag, useFeatureFlag } from 'utils/permissions/feature-flags';
import styles from '../../../styles/typography.module.css';
import { Banner } from './Banner';
import DynamicBanner, { useDynamicBanner } from './DynamicBanner';
import { BannerDefinition, BANNERS } from './definitions';
import { useHasValidCredentials } from './hooks';

export const Banners: React.FC = () => {
  const { adjustMarginTop } = useNavHeightContext();
  const dynamicBanner = useDynamicBanner();
  const permissions = usePermissions();
  const { user } = useUserContext();
  const disableBanners = useFeatureFlag(FeatureFlag.DisableBanners);

  const shouldHide = !user || disableBanners;

  const [closedBannerIds, setClosedBannerIds] = useState<string[]>(
    LocalStorageUtil.get<string[]>(GlobalStorageKeys.DismissedBanners) || []
  );

  const allCredentialsEnteredAndValid = useHasValidCredentials();

  // Filter out banners that should not be displayed
  const banners = React.useMemo(() => {
    return BANNERS.filter(
      (banner) =>
        user &&
        banner.shouldBeDisplayed(user, allCredentialsEnteredAndValid, permissions) &&
        !closedBannerIds.includes(banner.id)
    );
  }, [closedBannerIds, permissions, allCredentialsEnteredAndValid, user]);

  // Adjust the top margin as needed whenever the banners change
  useEffect(() => {
    if (shouldHide) {
      adjustMarginTop(0);
    } else {
      adjustMarginTop(dynamicBanner.showBanner ? BANNER_HEIGHT : banners.length * BANNER_HEIGHT);
    }
  }, [adjustMarginTop, banners, dynamicBanner.showBanner, shouldHide]);

  // Handler for closing banners
  const handleOnRemoveBanner = React.useCallback(
    (banner: BannerDefinition) => {
      setClosedBannerIds((closed) => {
        const dismissed = _.concat(closed, banner.id);
        LocalStorageUtil.set(GlobalStorageKeys.DismissedBanners, dismissed);
        return dismissed;
      });
    },
    [setClosedBannerIds]
  );

  if (shouldHide) {
    return null;
  }

  return (
    <div className={styles.font}>
      {/* Show the dynamic warning above all else */}
      {dynamicBanner.showBanner && dynamicBanner.flag && dynamicBanner.acknowledge ? (
        <DynamicBanner flag={dynamicBanner.flag} acknowledge={dynamicBanner.acknowledge} />
      ) : (
        banners.map((banner) => (
          <Banner
            key={banner.id}
            content={banner}
            onRemoveBanner={handleOnRemoveBanner}
            user={user}
          />
        ))
      )}
    </div>
  );
};
Banners.displayName = 'Banners';
